import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { CalendarMinus, CalendarPlus } from "lucide-react";
import { toast } from "react-toastify";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { selectPortalBookableOccurrences } from "../../features/portal/portal-slice";
import {
  bookOccurrence,
  fetchBookableOccurrences,
  fetchPortalOccurrences,
} from "../../features/portal/slice-extensions/portal-slice-extension";
import { BookableOccurrenceResponse } from "../../features/portal/contracts/BookableOccurrenceResponse";
import { BookingMode } from "../../features/sessions/enums/BookingMode";
import { usePortalVocabulary } from "../../features/portal/hooks/usePortalVocabulary";
import { toCancellationPolicy } from "../../features/organization/mappers/CancellationPolicyResponseMapper";
import { policySentence } from "../../features/portal/utils/policySentence";
import PortalSeatStatusChip from "./PortalSeatStatusChip";
import PortalCancelOccurrenceDialog from "./PortalCancelOccurrenceDialog";
import { formatTimeOfDay } from "../../features/utils/displayFormat";

interface PortalBookableOccurrencesCardProps {
  memberId: string;
  /** The portal schedule's window, refreshed after a booking so the new date shows there too. */
  scheduleRange: { from: string; to: string };
}

const keyOf = (o: BookableOccurrenceResponse) => `${o.bookingId}-${o.date}`;

/** Open Sessions' upcoming occurrences the member (or their ward) can book. */
const PortalBookableOccurrencesCard = ({ memberId, scheduleRange }: PortalBookableOccurrencesCardProps) => {
  const { t, i18n } = useTranslation();
  const vocabulary = usePortalVocabulary();
  const dispatch = useAppDispatch();
  const occurrences = useAppSelector(selectPortalBookableOccurrences(memberId));
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [cancelling, setCancelling] = useState<BookableOccurrenceResponse | null>(null);

  useEffect(() => {
    dispatch(fetchBookableOccurrences({ memberId }));
  }, [dispatch, memberId]);

  const formatDate = (date: string) =>
    new Intl.DateTimeFormat(i18n.language, { weekday: "short", day: "numeric", month: "long" }).format(
      new Date(`${date}T00:00:00`),
    );

  const refresh = () => {
    dispatch(fetchBookableOccurrences({ memberId }));
    dispatch(fetchPortalOccurrences({ memberId, from: scheduleRange.from, to: scheduleRange.to }));
  };

  const handleBook = async (occurrence: BookableOccurrenceResponse) => {
    setBusyKey(keyOf(occurrence));
    setError(null);
    const result = await dispatch(
      bookOccurrence({
        memberId,
        request: { bookingId: occurrence.bookingId, occurrenceDate: occurrence.date },
      }),
    );
    setBusyKey(null);
    if (bookOccurrence.fulfilled.match(result)) {
      dispatch(fetchPortalOccurrences({ memberId, from: scheduleRange.from, to: scheduleRange.to }));
    } else {
      setError(result.payload?.message ?? t("portal.booking.failed", "Booking failed. Please try again."));
      dispatch(fetchBookableOccurrences({ memberId }));
    }
  };

  if (!occurrences || occurrences.length === 0) {
    return null;
  }

  return (
    <div className="with-border mb-8 rounded-2xl bg-white p-4 dark:bg-primary-10">
      <h2 className="flex items-center gap-2 text-base font-semibold text-grey-20 dark:text-white">
        <CalendarPlus size={20} />
        {t("portal.booking.title", { ...vocabulary, defaultValue: "{{Sessions}} you can book" })}
      </h2>
      <ul className="mt-3 flex flex-col gap-2">
        {occurrences.map((occurrence) => (
          <BookableRow
            key={keyOf(occurrence)}
            occurrence={occurrence}
            dateLabel={formatDate(occurrence.date)}
            busy={busyKey === keyOf(occurrence)}
            disabled={busyKey !== null}
            onBook={() => handleBook(occurrence)}
            onCancel={() => setCancelling(occurrence)}
          />
        ))}
      </ul>
      {error && <p className="field-error mt-2">{error}</p>}
      {cancelling && (
        <PortalCancelOccurrenceDialog
          memberId={memberId}
          bookingId={cancelling.bookingId}
          date={cancelling.date}
          title={cancelling.title}
          dateLabel={formatDate(cancelling.date)}
          onClose={() => setCancelling(null)}
          onCancelled={() => {
            setCancelling(null);
            toast.success(t("portal.cancel.done"));
            refresh();
          }}
        />
      )}
    </div>
  );
};

interface BookableRowProps {
  occurrence: BookableOccurrenceResponse;
  dateLabel: string;
  busy: boolean;
  disabled: boolean;
  onBook: () => void;
  onCancel: () => void;
}

const BookableRow = ({ occurrence, dateLabel, busy, disabled, onBook, onCancel }: BookableRowProps) => {
  const { t, i18n } = useTranslation();
  const vocabulary = usePortalVocabulary();
  const time = formatTimeOfDay(occurrence.timeLocal, i18n.language);
  const isFull = occurrence.placesLeft === 0;

  return (
    <li className="flex flex-wrap items-center justify-between gap-2 rounded-2xl bg-grey-95 p-3 dark:bg-primary-20">
      <div className="min-w-0">
        <p className="font-medium text-grey-20 dark:text-white">{occurrence.title}</p>
        <p className="text-sm text-grey-40 dark:text-grey-80">
          {dateLabel} · {time} · {occurrence.durationMinutes} {t("portal.minutes", "min")}
          {occurrence.instructorName && ` · ${occurrence.instructorName}`}
        </p>
        {occurrence.placesLeft !== null && !occurrence.seatStatus && !isFull && (
          <p className="text-xs text-grey-40 dark:text-grey-80">
            {t("portal.booking.placesLeft", { count: occurrence.placesLeft })}
          </p>
        )}
        {occurrence.bookingMode === BookingMode.OpenWithConfirmation && !occurrence.seatStatus && (
          <p className="text-xs text-grey-40 dark:text-grey-80">
            {t("portal.booking.needsConfirmation", {
              ...vocabulary,
              defaultValue: "The {{instructor}} confirms this booking.",
            })}
          </p>
        )}
        <p className="text-xs text-grey-40 dark:text-grey-80">
          {policySentence(toCancellationPolicy(occurrence.cancellationPolicy), t, i18n.language)}
        </p>
      </div>
      {occurrence.seatStatus ? (
        <div className="flex items-center gap-2">
          <PortalSeatStatusChip status={occurrence.seatStatus} />
          <button type="button" className="btn btn-secondary" onClick={onCancel} disabled={disabled}>
            <CalendarMinus size={18} />
            {t("portal.cancel.action")}
          </button>
        </div>
      ) : isFull ? (
        <span className="rounded-full bg-grey-90 px-2 py-0.5 text-xs font-medium text-grey-30 dark:bg-primary-30 dark:text-grey-80">
          {t("portal.booking.full", "Full")}
        </span>
      ) : (
        <button type="button" className="btn btn-primary" onClick={onBook} disabled={disabled}>
          <CalendarPlus size={18} />
          {busy ? t("common.loading", "Loading…") : t("portal.booking.book", "Book")}
        </button>
      )}
    </li>
  );
};

export default PortalBookableOccurrencesCard;
